<template>
  <section
    class="flex items-center cursor-pointer hover:bg-bg-base-bg3 rounded-8 p-2 gap-2"
    :class="{ 'bg-bg-base-bg4': isSelected, 'hover:bg-accent!': darkTheme}"
    data-test="contact-list-item-member"
    @mouseenter.stop="onMouseEnter"
    @mouseover.stop="onMouseOver"
    @mouseleave.stop="onMouseLeave"
  >
    <div class="relative">
      <contact-avatar
        :contact="contact"
        :icon="itemIcon"
        :contact-type="contact.type"
      />
      <div
        v-if="contact.imLogin && imIsReady && !isBot"
      >
        <contact-status
          :p-served="contact.imLogin"
          compact-mode
        />
      </div>
    </div>

    <div class="grid">
      <p
        class="text-body-m-15-22 truncate mb-[2px] text-fg-base-fg3"
        :class="{'text-white': darkTheme}"
      >
        {{ contact.name || $t('common.noName') }}
      </p>

      <p
        v-if="!contact.imLogin"
        class="text-fg-base-fg22 text-body-r-14-20"
      >
        <span>{{ t('mainAside.externalContact') }}</span>
      </p>
      <div
        v-else-if="contact.position || contact.organization"
        class="grid text-body-r-14-20"
        :class="{ 'text-black-600': isSelected }"
      >
        <p
          v-if="contact.position"
          class="text-fg-base-fg22 truncate"
        >
          {{ contact.position }}
        </p>
        <p
          v-if="contact.organization"
          class="text-fg-base-fg22"
        >
          {{ contact.organization }}
        </p>
      </div>
    </div>

    <div
      v-if="showFavoriteButton && !isBot"
      class="ml-auto"
    >
      <wui-btn
        :class="contact.isFavorite ? 'text-surf-brand-base-def!' : 'text-fg-base-fg32! hover:text-fg-base-fg22!'"
        :prepend-icon="contact.isFavorite ? 'starF' : 'starEmptyM'"
        icon
        variant="neut"
        state="alpha"
        auto-loading
        data-test="contact-list-item-member-favorite-button"
        @click.stop="onToggle"
      />
    </div>

    <transition>
      <div
        v-if="isHover && (contact.name || contact.internalNumber)"
        class="py-2 px-4 space-y-1 rounded-12 shadow-raised bg-bg-base-bg1 fixed z-50 pointer-events-none"
        data-test="contact-tooltip"
        :style="{
          top: `${(mouseY || 0) + 10}px`,
          left: `${(mouseX || 0) + 10}px`,
        }"
      >
        <p
          v-if="contact.name"
          data-test="contact-tooltip-name"
          class="text-body-m-15-22"
        >
          {{ contact.name }}
        </p>
        <p
          v-if="contact.internalNumber && contact.internalNumber !== contact.name"
          data-test="contact-tooltip-internalnumber"
          class="text-body-r-14-20 text-fg-base-fg22"
        >
          {{ contact.internalNumber }}
        </p>
        <p
          v-if="contact.mobilePhone && contact.mobilePhone !== contact.name"
          data-test="contact-tooltip-internalnumber"
          class="text-body-r-14-20 text-fg-base-fg22"
        >
          {{ contact.mobilePhone }}
        </p>
      </div>
    </transition>
  </section>
</template>

<script lang="ts" setup>
import { useRenderNotifier, WuiBtn } from '@wui/common-library'
import { isBotPServed } from '@wui/im'
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

import { ContactAvatar } from '@/entities/contact-avatar'
import { useContact } from '@/entities/use-contact'

import { useStatusSubscribe } from '@/shared/composables/im'
import { useLocalization } from '@/shared/composables/localization'
import { ContactStatus } from '@/shared/contact-status'
import { useIMStore } from '@/shared/store/im'
import { Contact } from '@/shared/types/api'

const props = withDefaults(defineProps<{
  contact: Contact,
  isSelected?: boolean,
  isVisible?: boolean,
  showFavoriteButton?: boolean,
  darkTheme?: boolean,
}>(), {
  isSelected: false,
  darkTheme: false,
  isVisible: false,
  showFavoriteButton: true,
})

const mouseX = ref<number | null>(null)
const mouseY = ref<number | null>(null)
const isHover = ref(false)

const { notifyError } = useRenderNotifier()
const { imIsReady } = storeToRefs(useIMStore())

const { t } = useLocalization()

const isExternal = computed(() => {
  return !props.contact.imLogin
})

const isBot = computed(() => isBotPServed(props.contact.imLogin))

const itemIcon = computed(() => {
  if (!isExternal.value) {
    return undefined
  }

  return 'userM'
})

const onToggle = async () => {
  try {
    const { toggleFavorite } = useContact(props.contact)

    await toggleFavorite()
    onMouseLeave()
  } catch (e: unknown) {
    const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
    notifyError(errorMessage)
  } finally {
    isHover.value = false
  }
}

let debounceTimeout: NodeJS.Timeout | null = null
const onMouseEnter = () => {
  debounceTimeout = setTimeout(() => {
    isHover.value = true
  }, 500)
}

const onMouseOver = (e: MouseEvent) => {
  mouseX.value = e.clientX
  mouseY.value = e.clientY
}

const onMouseLeave = () => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout)
  }
  isHover.value = false
}

const { subscribe, unsubscribe } = useStatusSubscribe()

onMounted(() => {
  if (props.contact.imLogin) {
    subscribe(props.contact.imLogin)
  }
})

onUnmounted(() => {
  if (props.contact.imLogin) {
    unsubscribe(props.contact.imLogin)
  }
})

watch(() => props.isVisible, () => {
  if (!props.contact.imLogin) {
    return
  }
  if (props.isVisible) {
    subscribe(props.contact.imLogin)
  } else {
    unsubscribe(props.contact.imLogin)
  }
})
</script>

<style lang="css" scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
